import React, { useEffect, useState, useMemo } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const FREQ_TYPES = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'];
const TEAMS = ['ELECTRICAL', 'PLUMBING', 'HVAC', 'CARPENTRY', 'GENERAL'];
const CATEGORIES = ['ELECTRICAL', 'PLUMBING', 'HVAC', 'FIRE_SAFETY', 'ELEVATOR', 'POOL', 'GENERAL', 'OTHER'];

const emptyForm = {
  schedule_name: '',
  category: '',
  asset_id: '',
  frequency_type: 'MONTHLY',
  frequency_value: 1,
  assigned_team: 'GENERAL',
  next_due_date: '',
  checklist: [],
  is_active: true
};

function todayStr() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function MaintPreventive({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // execution history
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [execLoading, setExecLoading] = useState(false);

  const headers = useMemo(() => ({
    Authorization: 'Bearer ' + token,
    'x-chain-id': selectedProperty?.chain_id,
    'x-property-id': selectedProperty?.id
  }), [token, selectedProperty]);

  const jsonHeaders = useMemo(() => ({
    ...headers,
    'Content-Type': 'application/json'
  }), [headers]);

  /* ── data loading ── */

  const loadSchedules = async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/maintenance/pm-schedules', { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load PM schedules');
      setRows(Array.isArray(data) ? data : (data.rows || []));
    } catch {
      setError('Network error loading PM schedules');
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/maintenance/assets', { headers });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setAssets(Array.isArray(data) ? data : (data.rows || []));
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (selectedProperty?.id) {
      loadSchedules();
      loadAssets();
    } else {
      setRows([]);
      setAssets([]);
    }
  }, [selectedProperty, token]);

  /* ── create / edit ── */

  const openCreate = () => {
    setForm({ ...emptyForm, next_due_date: todayStr() });
    setEditingId(null);
    setError('');
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      schedule_name: row.schedule_name || '',
      category: row.category || '',
      asset_id: row.asset_id || '',
      frequency_type: row.frequency_type || 'MONTHLY',
      frequency_value: row.frequency_value || 1,
      assigned_team: row.assigned_team || 'GENERAL',
      next_due_date: (row.next_due_date || '').slice(0, 10),
      checklist: Array.isArray(row.checklist) ? row.checklist : [],
      is_active: row.is_active !== false
    });
    setEditingId(row.id);
    setError('');
    setShowModal(true);
  };

  const saveSchedule = async () => {
    setError('');
    if (!form.schedule_name.trim()) return setError('Schedule Name is required');
    if (!form.category) return setError('Category is required');
    if (!form.next_due_date) return setError('Next Due Date is required');
    if (!form.frequency_value || Number(form.frequency_value) < 1) return setError('Frequency Value must be at least 1');

    const body = {
      ...form,
      frequency_value: Number(form.frequency_value),
      asset_id: form.asset_id || null
    };

    try {
      const url = editingId
        ? `/api/v1/maintenance/pm-schedules/${editingId}`
        : '/api/v1/maintenance/pm-schedules';
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to save schedule');
      setShowModal(false);
      loadSchedules();
    } catch {
      setError('Network error saving schedule');
    }
  };

  /* ── toggle active ── */

  const toggleActive = async (row) => {
    try {
      const res = await fetch(`/api/v1/maintenance/pm-schedules/${row.id}`, {
        method: 'PUT',
        headers: jsonHeaders,
        body: JSON.stringify({ ...row, is_active: !row.is_active })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return setError(data.error || 'Failed to update schedule');
      }
      loadSchedules();
    } catch {
      setError('Network error toggling active status');
    }
  };

  /* ── execution history ── */

  const openExecutions = async (row) => {
    setSelectedSchedule(row);
    setExecutions([]);
    setExecLoading(true);
    try {
      const res = await fetch(`/api/v1/maintenance/pm-schedules/${row.id}/executions`, { headers });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setExecutions(Array.isArray(data) ? data : (data.rows || []));
      else setError(data.error || 'Failed to load execution history');
    } catch {
      setError('Network error loading execution history');
    } finally {
      setExecLoading(false);
    }
  };

  /* ── checklist helpers ── */

  const addChecklistItem = () => {
    setForm(f => ({ ...f, checklist: [...f.checklist, { text: '', mandatory: false }] }));
  };

  const updateChecklistItem = (idx, field, value) => {
    setForm(f => {
      const cl = [...f.checklist];
      cl[idx] = { ...cl[idx], [field]: value };
      return { ...f, checklist: cl };
    });
  };

  const removeChecklistItem = (idx) => {
    setForm(f => ({ ...f, checklist: f.checklist.filter((_, i) => i !== idx) }));
  };

  /* ── helpers ── */

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return dateStr.slice(0, 10) < todayStr();
  };

  /* ── styles ── */

  const S = {
    page: { padding: 24, fontFamily: 'Inter, sans-serif', color: '#1e293b', maxWidth: 1200, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, margin: 0 },
    btn: { padding: '8px 18px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
    btnPrimary: { background: '#2563eb', color: '#fff' },
    btnDanger: { background: '#ef4444', color: '#fff' },
    btnSecondary: { background: '#e2e8f0', color: '#334155' },
    btnSm: { padding: '4px 12px', fontSize: 13 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: { textAlign: 'left', padding: '10px 12px', background: '#f1f5f9', borderBottom: '2px solid #cbd5e1', fontWeight: 600, fontSize: 13, color: '#475569' },
    td: { padding: '10px 12px', borderBottom: '1px solid #e2e8f0' },
    tdOverdue: { padding: '10px 12px', borderBottom: '1px solid #e2e8f0', color: '#dc2626', fontWeight: 600 },
    rowOverdue: { background: '#fef2f2' },
    badge: (active) => ({
      display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: active ? '#dcfce7' : '#fee2e2', color: active ? '#166534' : '#991b1b'
    }),
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 28, width: isMobile ? '100%' : 560, maxWidth: isMobile ? '95vw' : undefined, boxSizing: isMobile ? 'border-box' : undefined, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
    modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 18, marginTop: 0 },
    formGroup: { marginBottom: 14 },
    label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#475569' },
    input: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box' },
    select: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 14, boxSizing: 'border-box', background: '#fff' },
    error: { background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 14 },
    checklistRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 },
    noData: { textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 15 },
    link: { color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, fontSize: 14 },
    execSection: { marginTop: 24, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20 },
    execTitle: { fontSize: 16, fontWeight: 700, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    toggleWrap: { display: 'flex', alignItems: 'center', gap: 8 },
    toggleTrack: (on) => ({
      width: 40, height: 22, borderRadius: 11, background: on ? '#2563eb' : '#cbd5e1',
      position: 'relative', cursor: 'pointer', transition: 'background 0.2s'
    }),
    toggleThumb: (on) => ({
      width: 18, height: 18, borderRadius: '50%', background: '#fff',
      position: 'absolute', top: 2, left: on ? 20 : 2, transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
    }),
    actions: { display: 'flex', gap: 6 }
  };

  /* ── render ── */

  if (!selectedProperty?.id) {
    return <div style={S.page}><p style={{ color: '#94a3b8' }}>Select a property from the header to manage Preventive Maintenance schedules.</p></div>;
  }

  return (
    <div style={S.page}>
      {/* header */}
      <div style={S.header}>
        <h2 style={S.title}>Preventive Maintenance Schedules</h2>
        <button style={{ ...S.btn, ...S.btnPrimary }} onClick={openCreate}>+ New Schedule</button>
      </div>

      {error && <div style={S.error}>{error} <span style={{ float: 'right', cursor: 'pointer' }} onClick={() => setError('')}>x</span></div>}

      {/* table */}
      {loading ? (
        <div style={S.noData}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={S.noData}>No PM schedules found. Click "+ New Schedule" to create one.</div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Name</th>
              <th style={S.th}>Category</th>
              <th style={S.th}>Frequency</th>
              <th style={S.th}>Next Due Date</th>
              <th style={S.th}>Last Executed</th>
              <th style={S.th}>Active</th>
              <th style={S.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const overdue = r.is_active && isOverdue(r.next_due_date);
              return (
                <tr key={r.id} style={overdue ? S.rowOverdue : undefined}>
                  <td style={overdue ? S.tdOverdue : S.td}>
                    <button style={S.link} onClick={() => openExecutions(r)}>{r.schedule_name}</button>
                  </td>
                  <td style={overdue ? S.tdOverdue : S.td}>{r.category}</td>
                  <td style={overdue ? S.tdOverdue : S.td}>
                    Every {r.frequency_value} {r.frequency_type}
                  </td>
                  <td style={overdue ? S.tdOverdue : S.td}>
                    {(r.next_due_date || '').slice(0, 10)}
                    {overdue && <span style={{ marginLeft: 6, fontSize: 11, background: '#dc2626', color: '#fff', padding: '1px 6px', borderRadius: 4 }}>OVERDUE</span>}
                  </td>
                  <td style={overdue ? S.tdOverdue : S.td}>{r.last_executed ? r.last_executed.slice(0, 10) : '--'}</td>
                  <td style={overdue ? S.tdOverdue : S.td}>
                    <div style={S.toggleWrap}>
                      <div style={S.toggleTrack(r.is_active)} onClick={() => toggleActive(r)}>
                        <div style={S.toggleThumb(r.is_active)} />
                      </div>
                      <span style={{ fontSize: 12 }}>{r.is_active ? 'Yes' : 'No'}</span>
                    </div>
                  </td>
                  <td style={overdue ? S.tdOverdue : S.td}>
                    <div style={S.actions}>
                      <button style={{ ...S.btn, ...S.btnSecondary, ...S.btnSm }} onClick={() => openEdit(r)}>Edit</button>
                      <button style={{ ...S.btn, ...S.btnSecondary, ...S.btnSm }} onClick={() => openExecutions(r)}>History</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* execution history panel */}
      {selectedSchedule && (
        <div style={S.execSection}>
          <div style={S.execTitle}>
            <span>Execution History: {selectedSchedule.schedule_name}</span>
            <button style={{ ...S.btn, ...S.btnSecondary, ...S.btnSm }} onClick={() => { setSelectedSchedule(null); setExecutions([]); }}>Close</button>
          </div>
          {execLoading ? (
            <div style={S.noData}>Loading executions...</div>
          ) : executions.length === 0 ? (
            <div style={S.noData}>No execution records found for this schedule.</div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Executed On</th>
                  <th style={S.th}>Executed By</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((ex, i) => (
                  <tr key={ex.id || i}>
                    <td style={S.td}>{ex.executed_at ? ex.executed_at.slice(0, 16).replace('T', ' ') : '--'}</td>
                    <td style={S.td}>{ex.executed_by_name || ex.executed_by || '--'}</td>
                    <td style={S.td}>
                      <span style={S.badge(ex.status === 'COMPLETED')}>{ex.status || 'N/A'}</span>
                    </td>
                    <td style={S.td}>{ex.remarks || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* create / edit modal */}
      {showModal && (
        <div style={S.overlay} onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={S.modal}>
            <h3 style={S.modalTitle}>{editingId ? 'Edit PM Schedule' : 'New PM Schedule'}</h3>

            {error && <div style={S.error}>{error}</div>}

            <div style={S.formGroup}>
              <label style={S.label}>Schedule Name *</label>
              <input style={S.input} value={form.schedule_name}
                onChange={e => setForm(f => ({ ...f, schedule_name: e.target.value }))} placeholder="e.g. Monthly HVAC Filter Check" />
            </div>

            <div style={S.formGroup}>
              <label style={S.label}>Category *</label>
              <select style={S.select} value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                <option value="">-- Select --</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={S.formGroup}>
              <label style={S.label}>Asset (optional)</label>
              <select style={S.select} value={form.asset_id}
                onChange={e => setForm(f => ({ ...f, asset_id: e.target.value }))}>
                <option value="">-- None --</option>
                {assets.map(a => <option key={a.id} value={a.id}>{a.asset_name || a.name} {a.location ? `(${a.location})` : ''}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ ...S.formGroup, flex: 1 }}>
                <label style={S.label}>Frequency Type *</label>
                <select style={S.select} value={form.frequency_type}
                  onChange={e => setForm(f => ({ ...f, frequency_type: e.target.value }))}>
                  {FREQ_TYPES.map(ft => <option key={ft} value={ft}>{ft}</option>)}
                </select>
              </div>
              <div style={{ ...S.formGroup, flex: 1 }}>
                <label style={S.label}>Frequency Value *</label>
                <input type="number" min="1" style={S.input} value={form.frequency_value}
                  onChange={e => setForm(f => ({ ...f, frequency_value: e.target.value }))} />
              </div>
            </div>

            <div style={S.formGroup}>
              <label style={S.label}>Assigned Team *</label>
              <select style={S.select} value={form.assigned_team}
                onChange={e => setForm(f => ({ ...f, assigned_team: e.target.value }))}>
                {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div style={S.formGroup}>
              <label style={S.label}>Next Due Date *</label>
              <input type="date" style={S.input} value={form.next_due_date}
                onChange={e => setForm(f => ({ ...f, next_due_date: e.target.value }))} />
            </div>

            {/* checklist */}
            <div style={S.formGroup}>
              <label style={S.label}>Checklist Items</label>
              {form.checklist.map((item, idx) => (
                <div key={idx} style={S.checklistRow}>
                  <input style={{ ...S.input, flex: 1 }} value={item.text} placeholder={`Item ${idx + 1}`}
                    onChange={e => updateChecklistItem(idx, 'text', e.target.value)} />
                  <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={item.mandatory}
                      onChange={e => updateChecklistItem(idx, 'mandatory', e.target.checked)} />
                    Mandatory
                  </label>
                  <button style={{ ...S.btn, ...S.btnDanger, ...S.btnSm }} onClick={() => removeChecklistItem(idx)}>X</button>
                </div>
              ))}
              <button style={{ ...S.btn, ...S.btnSecondary, ...S.btnSm, marginTop: 4 }} onClick={addChecklistItem}>+ Add Item</button>
            </div>

            {/* is_active toggle */}
            <div style={{ ...S.formGroup, ...S.toggleWrap }}>
              <label style={{ ...S.label, marginBottom: 0 }}>Is Active</label>
              <div style={S.toggleTrack(form.is_active)} onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}>
                <div style={S.toggleThumb(form.is_active)} />
              </div>
              <span style={{ fontSize: 13 }}>{form.is_active ? 'Yes' : 'No'}</span>
            </div>

            {/* modal actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button style={{ ...S.btn, ...S.btnSecondary }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...S.btn, ...S.btnPrimary }} onClick={saveSchedule}>{editingId ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintPreventive;
